import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import WeightDetailDialogNew from 'src/pages-new/patient/tabs/weight/WeightDetailDialogNew'

interface WeightTabProps {
  patientId: string
  firstName?: string
  lastName?: string
}

interface WeightRow {
  _id: number
  date?: string
  weight?: string | number
  [key: string]: any
}

interface WeightColumnConfig {
  id: string
  label: string
}

const DEFAULT_COLUMNS: WeightColumnConfig[] = [
  {id: 'date', label: 'DATA'},
  {id: 'lastName', label: 'COGNOME'},
  {id: 'firstName', label: 'NOME'},
  {id: 'weight', label: 'PESO'},
]

const FILTER_CONFIG: FilterItem[] = [
  {id: 'date', label: 'Data', type: 'rangePicker'},
]

function renderCell(id: string) {
  return (row: WeightRow): React.ReactNode => {
    if (id === 'date') {
      return <Typography variant="body2">{row.date ? moment(row.date).format('ddd DD MMM YYYY') : '—'}</Typography>
    }
    if (id === 'weight') {
      return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <ScaleOutlinedIcon sx={{fontSize: 16, color: 'primary.main'}} />
          <Typography variant="body2">{row.weight ?? '—'}</Typography>
        </Box>
      )
    }
    return <Typography variant="body2">{row[id] ?? '—'}</Typography>
  }
}

function buildColumns(columnConfig: WeightColumnConfig[]): ColumnDef<WeightRow>[] {
  return columnConfig.map(col => ({
    key: col.id,
    header: col.label,
    sortKey: col.id === 'date' ? 'date' : undefined,
    render: renderCell(col.id),
    hideInMobileCard: col.id === 'firstName' || col.id === 'lastName',
  }))
}

function mobileCardTitle(row: WeightRow): string {
  return `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || '—'
}

export default function WeightTab({patientId, firstName, lastName}: WeightTabProps): JSX.Element {
  const weightColumnOptions: WeightColumnConfig[] = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.weightConfiguration?.columnOptions
  )

  const columns = weightColumnOptions?.length > 0 ? buildColumns(weightColumnOptions) : buildColumns(DEFAULT_COLUMNS)
  // Only show the mobile card header (patient name) when firstName/lastName survived the
  // PSP's columnOptions (or no config was set at all) — otherwise a disabled column would
  // still show up there.
  const hasNameColumn = columns.some(col => col.key === 'firstName' || col.key === 'lastName')

  const [rows, setRows] = useState<WeightRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-date'})
  const [filters, setFilters] = useState<FilterResult[]>([])
  const [weightDetail, setWeightDetail] = useState<WeightRow>()
  const [openDetailDialog, setOpenDetailDialog] = useState(false)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getPatientWeights(patientId, filters, pagination, setStatus),
      apiClient.getPatientWeightsCount(patientId, filters, setStatusCount),
    ])
      .then(([weights, countResult]) => {
        if (Array.isArray(weights)) {
          setRows(weights.map((weight: any, i: number) => ({
            ...weight,
            firstName,
            lastName,
            _id: i,
          })))
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, filters, pagination, firstName, lastName])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Belt-and-suspenders: `pagination.order` is sent to the backend via getPatientWeights,
  // but also re-sort the already-fetched page client-side so the "DATA" arrow always
  // visibly reorders rows even if the API/mock ignores the `sort` param.
  const sortedRows = useMemo(() => {
    const order = pagination.order
    if (!order || (order !== 'date' && order !== '-date')) return rows
    const desc = order.startsWith('-')
    return [...rows].sort((a, b) => {
      const aTime = a.date ? new Date(a.date).getTime() : 0
      const bTime = b.date ? new Date(b.date).getTime() : 0
      return desc ? bTime - aTime : aTime - bTime
    })
  }, [rows, pagination.order])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleRowClick = (row: WeightRow) => {
    setWeightDetail(row)
    setOpenDetailDialog(true)
  }

  return (
    <Box>
      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i dati.'}
        </InlineMessage>
      )}

      <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
        <Filters config={FILTER_CONFIG} onSubmit={handleFiltersSubmit} />
      </Box>

      <DataTable<WeightRow>
        columns={columns}
        rows={sortedRows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun dato trovato"
        onRowClick={handleRowClick}
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortBy={pagination.order}
        onSortChange={(order) => setPagination(prev => ({...prev, order, page: 0}))}
        mobileCardTitle={hasNameColumn ? mobileCardTitle : undefined}
      />

      <WeightDetailDialogNew
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        detail={weightDetail}
      />
    </Box>
  )
}
